import React, {useCallback, useState} from "react";
import {useAppSelector} from "../../../app/hooks";
import {ChatEngine} from "../../../types";
import {
    createPendingPasteAttachment,
    isAttachablePaste,
    MAX_PASTE_ATTACHMENTS,
    normalizePastedText,
    PendingPasteAttachment,
} from "../utils/pasteAttachments";

/**
 * Holds the pastes an input has captured as attachments. A paste longer than
 * the line threshold never reaches the textarea; shorter ones paste normally.
 *
 * The experimental engine has no attachment support, so a paste stays inline
 * text there rather than becoming a chip the request would leave behind.
 */
export function usePasteAttachments() {
    const [attachments, setAttachments] = useState<PendingPasteAttachment[]>(
        []
    );
    const chatEngine = useAppSelector(
        (state) => state.session.chosenChatEngine
    );
    const attachmentsSupported = chatEngine !== ChatEngine.Experimental;

    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!attachmentsSupported) return;
            const text = normalizePastedText(
                event.clipboardData.getData("text/plain")
            );
            if (!text || !isAttachablePaste(text)) return;
            // The paste is an attachment, so it never lands in the textarea,
            // even when the limit or a duplicate turns it away below.
            event.preventDefault();
            setAttachments((current) => {
                if (current.length >= MAX_PASTE_ATTACHMENTS) return current;
                const pending = createPendingPasteAttachment(text);
                const isDuplicate = current.some(
                    (attachment) => attachment.content === pending.content
                );
                return isDuplicate ? current : [...current, pending];
            });
        },
        [attachmentsSupported]
    );

    const removeAttachment = useCallback((id: string) => {
        setAttachments((current) =>
            current.filter((attachment) => attachment.id !== id)
        );
    }, []);

    const clearAttachments = useCallback(() => setAttachments([]), []);

    return {attachments, handlePaste, removeAttachment, clearAttachments};
}
